import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebounced } from '@/components/parameters/useDebounced';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { Pager } from '@/components/Pager';
import { StatusPill } from '@/components/StatusPill';
import { StatusFilterBar } from '@/components/StatusFilterBar';
import { isRunStatus, statusFilterOrder } from '@/components/status';
import { describeDelivery } from '@/components/pickupFormat';
import { subscriptionTitle } from '@/types';
import type {
  SubscriptionPickupState,
  SubscriptionRunStatus,
  SubscriptionStatusCount,
  SubscriptionSummary
} from '@/types';
import { HelpLink } from '@/components/HelpLink';

/** Matches the KT Insights export history and report pickup, so every list here pages alike. */
const pageSize = 50;

/**
 * How many "run now" requests the bulk action has in flight at once.
 *
 * Not a throttle for the browser's sake — MANDW01 serialises this work, so firing all fifty at a
 * failed-overnight selection does not run them any sooner, it queues them all behind each other
 * and makes the report server slower for everybody else meanwhile (the same effect measured on the
 * parameter-value lookups: five concurrent took 7-16s each against 1-2s alone). Two keeps the
 * server busy without a burst, and means a slow one never blocks more than one other.
 */
const RUN_NOW_CONCURRENCY = 2;

/** One subscription's result from a bulk run. `error` null means the report server accepted it. */
type RunOutcome = { id: string; title: string; error: string | null };

/** A bulk run in progress or just finished. Kept until dismissed, so the failures stay readable. */
type BulkRun = { total: number; outcomes: RunOutcome[]; finished: boolean };

/**
 * The folder a report lives in — the last segment of its path before the report itself.
 *
 * Not a label. The portal has no concept of one: nothing on the report server's catalogue item, the
 * subscription, or the portal's own database carries a tag or category, and the folder is the only
 * grouping that already exists. It is what people say out loud ("the Scope 3 one"), it costs no new
 * data, and it fits on one line where the full path takes three.
 *
 * Returns null for a report at the root, where there is no folder to name, rather than an empty
 * chip. Paths are report-server paths and always use forward slashes.
 */
function reportFolder(reportPath: string): string | null {
  const segments = reportPath.split('/').filter(Boolean);
  return segments.length >= 2 ? segments[segments.length - 2] : null;
}

export function MySubscriptions() {
  const api = useApi();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();

  /**
   * Where in the list we are.
   *
   * State rather than the query string, unlike the filters — the same split the KT Insights
   * export history makes. A filtered view is worth pasting to a colleague; "page 4" of a list
   * that will have moved on by the time they open it is not.
   */
  const [skip, setSkip] = useState(0);

  // Deliberately NOT in the query string either, and for a stronger reason: a set of rows somebody
  // ticked is about to be acted on and has no meaning once it has been. Keyed by id but holding the
  // whole row, because the rows are no longer all in memory — see runPicked.
  const [picked, setPicked] = useState<Map<string, SubscriptionSummary>>(new Map());
  const [bulkRun, setBulkRun] = useState<BulkRun | null>(null);
  const [running, setRunning] = useState(false);

  /**
   * Who is signed in, purely so an admin can narrow this page back down to their own rows.
   *
   * `/subscriptions/mine` widens to the whole estate for an admin and says nothing about having
   * done so (SubscriptionService.ListMineAsync passes a null owner), so the page cannot tell a
   * 286-row admin view from a 286-row personal one without asking. Already cached under this key
   * elsewhere with the same staleTime, so it costs no extra request.
   */
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: Infinity });

  const search = params.get('q') ?? '';
  const mineOnly = params.get('owner') === 'me';
  const selected = new Set((params.get('status') ?? '').split(',').filter(isRunStatus));

  // Filters live in the query string so a filtered view survives the trip into a
  // subscription and back — the main way anyone leaves this page — and can be
  // pasted to a colleague.
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  /**
   * The search box is driven from local state and only writes the query string — and now only asks
   * the API — once typing settles.
   *
   * Writing it per keystroke is what made this page feel sluggish on the live estate. Every
   * character called setParams, which is a router navigation, which re-renders the whole route
   * before the browser could paint the character that was just typed. The URL is there so a
   * filtered view can be shared and survives the trip into a subscription; neither of those needs
   * it to be accurate mid-word.
   *
   * The search itself now runs on the SERVER, over the whole list rather than the fifty rows that
   * happen to be loaded — so unlike before, the results wait for the debounce too. That is the
   * trade paging forces and it is the right way round: a search of the loaded page would silently
   * fail to find the subscription somebody is looking for, which is worse than finding it 250ms
   * later. `keepPreviousData` below is what stops the table blanking while it waits.
   */
  const [searchInput, setSearchInput] = useState(search);
  const settledSearch = useDebounced(searchInput, 250);

  useEffect(() => {
    if (settledSearch !== search) setParam('q', settledSearch);
    // `search` is deliberately not a dependency: this effect exists to push the local value OUT to
    // the URL. Reacting to the URL as well would make the two chase each other on every write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSearch]);

  // The other direction, for the cases the box cannot cause: a pasted link, the back button, or
  // "Clear filters". Guarded on inequality so it never fights normal typing.
  useEffect(() => {
    setSearchInput(current => (current.trim().toLowerCase() === search.trim().toLowerCase() ? current : search));
  }, [search]);

  /**
   * The status selection in the canonical order the chips are written in, so that Failed+Unknown
   * ticked in either order is one cache entry and one request rather than two.
   */
  const statusParam = useMemo(
    () => statusFilterOrder.filter(s => selected.has(s)).join(','),
    // `selected` is rebuilt from the URL on every render, so keying on it directly would defeat the
    // memo; the string it came from is the stable thing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.get('status')]
  );

  // Any change to the filters invalidates the current offset — page 4 of the old result set is
  // meaningless against the new one, and worse, it is usually past the end of it, so the table
  // would come back empty and look like "nothing matches".
  useEffect(() => setSkip(0), [settledSearch, statusParam, mineOnly]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['mine', settledSearch, statusParam, mineOnly, skip],
    queryFn: () => api.listMine({
      search: settledSearch || undefined,
      status: statusParam || undefined,
      mine: mineOnly,
      skip,
      take: pageSize
    }),
    // Without this the table empties on every page step and every keystroke that settles, and the
    // layout jumps.
    placeholderData: keepPreviousData,
    refetchInterval: 10_000 // keep last/next run fresh while the dev scheduler fires
  });

  const toggleStatus = (status: SubscriptionRunStatus) => {
    const next = new Set(selected);
    if (next.has(status)) next.delete(status); else next.add(status);
    setParam('status', statusFilterOrder.filter(s => next.has(s)).join(','));
  };

  const clearAll = () => {
    // The box first, so the list clears on this render rather than waiting for the debounce to
    // push an empty string out and the URL to come back.
    setSearchInput('');
    // Explicitly, rather than leaving it to the effect above: clearing only the search box would
    // otherwise hold the old offset for the length of the debounce, and the one request made in
    // that window asks for page 4 of a list that no longer has one.
    setSkip(0);
    const next = new URLSearchParams(params);
    next.delete('q');
    next.delete('status');
    next.delete('owner');
    setParams(next, { replace: true });
  };

  if (isLoading) return <div className="placeholder">Loading subscriptions...</div>;
  if (error) return <div className="error-banner">Could not load: {(error as Error).message}</div>;

  const rows = data?.subscriptions ?? [];

  /**
   * The numbers on screen all come from the API now, and none of them can be recovered from `rows`.
   *
   * `total` is how many match the filters — what the pager steps through. `totalUnfiltered` is
   * every subscription this caller may see. `statusCounts` is per-status over the searched set
   * ignoring the status chips, so the sum across it is what the "All" chip reads and toggling a
   * chip does not move the other numbers. Every one of those used to be a count of the loaded
   * array, and every one of them would now be a count of fifty rows.
   */
  const total = data?.total ?? 0;
  const totalUnfiltered = data?.totalUnfiltered ?? 0;
  const counts = toCountMap(data?.statusCounts ?? []);
  const searchedTotal = (data?.statusCounts ?? []).reduce((sum, c) => sum + c.total, 0);
  const mineCount = data?.mineCount ?? 0;

  // The report server refuses to run a disabled subscription, so it is never offered — a checkbox
  // that ticks and then reports a failure would be the page pretending it did not already know.
  const runnableRows = rows.filter(r => r.isEnabled);
  const allRunnablePicked = runnableRows.length > 0 && runnableRows.every(r => picked.has(r.subscriptionId));
  const disabledInView = rows.length - runnableRows.length;

  /**
   * Ticking keeps the whole row, not just its id.
   *
   * The selection deliberately survives paging — tick three failures here, step to the next page,
   * tick two more, run all five — and once it does, the ids alone are not enough: the rows from
   * page 1 are no longer in `rows` when Run is pressed, so a `filter` over the current page would
   * silently run two of the five. Holding the row itself is what makes the count on the button and
   * the number of subscriptions actually started the same number.
   */
  function togglePicked(sub: SubscriptionSummary) {
    setPicked(prev => {
      const next = new Map(prev);
      if (next.has(sub.subscriptionId)) next.delete(sub.subscriptionId);
      else next.set(sub.subscriptionId, sub);
      return next;
    });
  }

  /**
   * Runs everything currently ticked, {@link RUN_NOW_CONCURRENCY} at a time.
   *
   * Each result is recorded as it lands rather than at the end, so the panel counts up while the
   * queue drains — a selection of twenty is a couple of minutes of the user watching nothing
   * otherwise. One failure never stops the rest: they are independent subscriptions and the whole
   * point of the action is clearing a morning's failures in one go.
   */
  async function runPicked() {
    // Straight off the selection, not off the loaded page — see togglePicked.
    const targets = [...picked.values()].filter(r => r.isEnabled);
    if (targets.length === 0) return;

    setBulkRun({ total: targets.length, outcomes: [], finished: false });
    setRunning(true);

    const queue = [...targets];
    const workers = Array.from({ length: Math.min(RUN_NOW_CONCURRENCY, queue.length) }, async () => {
      for (let next = queue.shift(); next; next = queue.shift()) {
        let failure: string | null = null;
        try {
          await api.runNow(next.subscriptionId);
        } catch (e) {
          failure = (e as Error).message.replace(/^API \d+: /, '');
        }
        const outcome: RunOutcome = { id: next.subscriptionId, title: subscriptionTitle(next), error: failure };
        setBulkRun(prev => prev && { ...prev, outcomes: [...prev.outcomes, outcome] });
      }
    });

    await Promise.all(workers);

    setRunning(false);
    setBulkRun(prev => prev && { ...prev, finished: true });
    setPicked(new Map());
    // Last run and status are now stale for every row that ran, and the dev outbox has new mail.
    // The key is a prefix, so this invalidates every page and filter combination, not just this one.
    void qc.invalidateQueries({ queryKey: ['mine'] });
    void qc.invalidateQueries({ queryKey: ['outbox'] });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>My Subscriptions</h1>
          {/* Both numbers come from the API, and neither is `rows.length`: that is now the size of
              one page, so a header built from it would read "50 subscriptions" for an estate of
              282 and never change. */}
          <div className="sub">
            {total === totalUnfiltered
              ? `${total} subscription${total === 1 ? '' : 's'}`
              : `Showing ${total} of ${totalUnfiltered} subscriptions`}
            {' · '}<HelpLink slug="check-a-report-was-sent" />
          </div>
        </div>
        <Link to="/subscriptions/new" className="btn-primary">+ New subscription</Link>
      </div>

      <div className="list-filters">
        <input
          className="input"
          style={{ maxWidth: 360 }}
          type="search"
          placeholder="Search by name, report, path or recipient..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        {/* Admins only, because for everybody else the endpoint has already narrowed the list and
            a chip that can never change anything is just noise. */}
        {me?.isAdmin && (
          <div className="status-filters" role="group" aria-label="Filter by owner">
            <button
              type="button"
              className={`pill status-filter owner-filter${mineOnly ? ' on' : ''}`}
              aria-pressed={mineOnly}
              title="You are an admin, so this page lists every subscription in the estate. This narrows it to the ones the portal records as yours."
              onClick={() => setParam('owner', mineOnly ? '' : 'me')}
            >
              Just mine<span className="count">{mineCount}</span>
            </button>
          </div>
        )}
        {/* `searchedTotal` is the sum of the roll-up, which is what the All chip means: everything
            the search matched, before any chip narrowed it. Summing the server's own counts rather
            than sending a separate number keeps the All chip and the individual chips arithmetically
            consistent by construction. */}
        <StatusFilterBar
          counts={counts}
          total={searchedTotal}
          selected={selected}
          onToggle={toggleStatus}
          onClear={() => setParam('status', '')}
        />
      </div>

      {/* The selection bar and the Failed chip are meant to be used together: filter to Failed,
          tick the header box, run. It sits above the table rather than in the header row so it
          appears where the eye already is after ticking something. */}
      {picked.size > 0 && (
        <div className="list-filters">
          <button className="btn-primary" onClick={() => void runPicked()} disabled={running}>
            {running ? 'Running...' : `Run now (${picked.size})`}
          </button>
          <button className="btn-ghost btn-sm" onClick={() => setPicked(new Map())} disabled={running}>
            Clear selection
          </button>
        </div>
      )}

      {bulkRun && <BulkRunPanel run={bulkRun} onDismiss={() => setBulkRun(null)} />}

      <div className="card">
        <table className="data-table data-table--compact">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input
                  type="checkbox"
                  checked={allRunnablePicked}
                  disabled={runnableRows.length === 0 || running}
                  // Adds this page's runnable rows to the selection and clears only those again,
                  // rather than replacing the whole selection: ticking here after ticking rows on
                  // an earlier page should add to what is already chosen, not throw it away.
                  onChange={() => setPicked(prev => {
                    const next = new Map(prev);
                    for (const r of runnableRows) {
                      if (allRunnablePicked) next.delete(r.subscriptionId);
                      else next.set(r.subscriptionId, r);
                    }
                    return next;
                  })}
                  aria-label="Select every subscription on this page that can be run"
                  title={disabledInView > 0
                    ? `Selects the ${runnableRows.length} on this page that can be run — ${disabledInView} disabled excluded`
                    : 'Selects every subscription on this page. Rows ticked on other pages stay ticked.'}
                />
              </th>
              <th>Report</th>
              <th>Created by</th>
              <th>Schedule</th>
              <th>Recipients</th>
              {/* Next to the recipients, because it answers the same question: where does this
                  report end up? Someone scanning the list should not have to open a subscription
                  to find out that it also files to pickup — or that it has stopped. */}
              <th>Delivers to</th>
              <th>Last run</th>
              <th>Next run</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="placeholder">
                  {/* `totalUnfiltered`, not the loaded rows: an empty page of a non-empty estate is
                      a filter that matched nothing, and telling that person to "create your first
                      one" would be flatly untrue. */}
                  {totalUnfiltered === 0 ? 'No subscriptions yet. Create your first one.' : (
                    <>
                      Nothing matches the current filters.{' '}
                      <button className="btn-ghost btn-sm" onClick={clearAll}>Clear filters</button>
                    </>
                  )}
                </td>
              </tr>
            ) : rows.map(r => (
              <Row
                key={r.subscriptionId}
                sub={r}
                picked={picked.has(r.subscriptionId)}
                onPick={() => togglePicked(r)}
                busy={running}
              />
            ))}
          </tbody>
        </table>
      </div>

      <Pager
        skip={skip}
        take={pageSize}
        shown={rows.length}
        total={total}
        busy={isFetching}
        onChange={setSkip}
      />
    </>
  );
}

/**
 * The server's status roll-up in the shape StatusFilterBar takes. A Map rather than the array
 * because the bar looks statuses up by name to decide which chips to offer at all.
 */
function toCountMap(counts: readonly SubscriptionStatusCount[]) {
  return new Map<SubscriptionRunStatus, number>(counts.map(c => [c.status, c.total]));
}

function Row({
  sub,
  picked,
  onPick,
  busy
}: {
  sub: SubscriptionSummary;
  picked: boolean;
  onPick: () => void;
  busy: boolean;
}) {
  const navigate = useNavigate();
  return (
    <tr onClick={() => navigate(`/subscriptions/${sub.subscriptionId}`)}>
      {/* stopPropagation, or ticking a box would open the subscription instead — the row click is
          the main way into this page's detail view and has to keep working alongside selection. */}
      <td className="select-cell" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={picked}
          disabled={!sub.isEnabled || busy}
          onChange={onPick}
          aria-label={`Select ${subscriptionTitle(sub)}`}
          title={sub.isEnabled ? undefined : 'Disabled subscriptions cannot be run'}
        />
      </td>
      {/* The subscription's own name leads when it has one; the folder underneath says which
          report it actually runs, without the full path's three wrapped monospace lines. The
          whole path is on the cell's title, and the detail page shows it in full. */}
      <td data-label="" title={sub.reportPath}>
        <div className="report-name">{subscriptionTitle(sub)}</div>
        {reportFolder(sub.reportPath) && (
          <div className="chip-row">
            <span className="chip chip-folder">{reportFolder(sub.reportPath)}</span>
          </div>
        )}
      </td>
      <td data-label="Created by">{sub.ownerDisplayName ?? '—'}</td>
      <td data-label="Schedule">{sub.scheduleDescription}</td>
      <td data-label="Recipients"><Recipients first={sub.recipientPreview} count={sub.recipientCount} /></td>
      <td data-label="Delivers to"><DeliversTo pickup={sub.pickup} /></td>
      <td data-label="Last run">{sub.lastRunUtc ? new Date(sub.lastRunUtc).toLocaleString() : '—'}</td>
      <td data-label="Next run">{sub.isEnabled && sub.nextRunUtc ? <NextRun iso={sub.nextRunUtc} /> : '—'}</td>
      <td data-label="Status"><StatusPill status={sub.lastStatus} /></td>
    </tr>
  );
}

/**
 * What a bulk run is doing, and what came of it.
 *
 * Says "started", never "sent". A run-now is answered 202 by the report server the moment it
 * accepts the request — the report still has to render and the mail still has to leave, and this
 * panel has no way to know whether either happened. The failures listed here are the ones that
 * never got that far, each with the report server's own words, because "3 failed" without saying
 * which three is an instruction to go and open all twenty.
 */
function BulkRunPanel({ run, onDismiss }: { run: BulkRun; onDismiss: () => void }) {
  const failures = run.outcomes.filter(o => o.error);
  const started = run.outcomes.length - failures.length;

  if (!run.finished) {
    return (
      <div className="notice" style={{ border: '1px solid var(--kt-grey-200)' }}>
        <strong>Starting {run.total} subscription{run.total === 1 ? '' : 's'}...</strong>
        <div>
          {run.outcomes.length} of {run.total} done. Sent {RUN_NOW_CONCURRENCY} at a time, so the
          report server keeps working through them rather than being asked for all of them at once.
        </div>
      </div>
    );
  }

  return (
    <div className={failures.length > 0 ? 'notice notice-warn' : 'notice'}
      style={failures.length > 0 ? undefined : { border: '1px solid var(--kt-grey-200)' }}>
      <strong>
        Started {started} of {run.total} subscription{run.total === 1 ? '' : 's'}.
        {failures.length > 0 && ` ${failures.length} could not be started.`}
      </strong>
      {failures.length > 0 ? (
        <ul className="mismatch-list">
          {failures.map(f => (
            <li key={f.id}>
              <strong style={{ display: 'inline' }}>{f.title}</strong>
              <span className="mismatch-note">{f.error}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div>
          Each one has been accepted by the report server. Delivery is not instant — the status and
          last-run columns update once the runs finish.
        </div>
      )}
      <button className="btn-secondary btn-sm" style={{ marginTop: 10 }} onClick={onDismiss}>Dismiss</button>
    </div>
  );
}

/** Recipients cell: the first address, with the rest rolled up into a "+N more" hint. */
function Recipients({ first, count }: { first?: string | null; count: number }) {
  if (!first) return <>{count > 0 ? `${count} recipients` : '—'}</>;
  return (
    <span title={`${count} ${count === 1 ? 'recipient' : 'recipients'}`}>
      {first}
      {count > 1 && (
        <span style={{ marginLeft: 6, color: 'var(--kt-grey-500)', fontSize: '.78rem' }}>
          +{count - 1} more
        </span>
      )}
    </span>
  );
}

/**
 * Delivery cell: where this subscription's output goes.
 *
 * Email-only is plain text rather than a pill. Almost every subscription is email-only, and a pill
 * on all 282 of them would make the pickup ones — the whole reason for the column — harder to spot,
 * not easier. A broken pickup route gets the same red pill the run status uses, because it means
 * the same thing: something that should be arriving is not.
 */
function DeliversTo({ pickup }: { pickup?: SubscriptionPickupState | null }) {
  const { label, tone, detail } = describeDelivery(pickup);

  if (tone === 'none') {
    return <span style={{ color: 'var(--kt-grey-500)' }}>{label}</span>;
  }

  return (
    <span className={`pill pill-${tone}`} title={detail ?? undefined}>{label}</span>
  );
}

/** Next-run cell: absolute time plus a friendly "in Xm" when it's imminent. */
function NextRun({ iso }: { iso: string }) {
  const at = new Date(iso);
  const mins = Math.round((at.getTime() - Date.now()) / 60_000);
  const soon = mins >= 0 && mins <= 60;
  return (
    <span>
      {at.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
      {soon && (
        <span style={{ marginLeft: 6, color: 'var(--kt-green)', fontWeight: 700, fontSize: '.78rem' }}>
          in {mins <= 1 ? '≤1m' : `${mins}m`}
        </span>
      )}
    </span>
  );
}
